import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import Swal from "sweetalert2";

import { PermisosService } from "../permisos.service";
import { FirmaFrcComponent } from "../../vacaciones/firma-frc/firma-frc.component";
import { FrcFirmadoComponent } from "../../vacaciones/frc-firmado/frc-firmado.component";
import { generaFrc017 } from "../frc017";
import { semanasHabiles } from "../../vacaciones/semanas-habiles";
import { GOCES, GocePermiso, Permiso } from "../../shared/model/permisos/permiso.model";

/** Una celda del mes. `iso` en null cuando sólo rellena la cuadrícula. */
interface DiaPermiso {
  iso: string | null;
  numero: number | null;
  esHoy: boolean;
  permisos: Permiso[];
}

/**
 * El calendario de permisos: quién falta, sale antes o llega tarde, mes
 * por mes.
 *
 * Es la vista de quien administra, y va aparte del calendario de
 * vacaciones a propósito. Podrían haberse pintado juntos, pero son dos
 * cosas que se leen distinto: las vacaciones son periodos de días que
 * ocupan una franja, y un permiso es un punto en un día. Mezclarlos
 * habría hecho ilegibles los dos.
 */
@Component({
  selector: "app-permisos-calendario",
  standalone: true,
  templateUrl: "./permisos-calendario.component.html",
  styleUrls: ["./permisos-calendario.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    FirmaFrcComponent,
    FrcFirmadoComponent,
  ],
})
export class PermisosCalendarioComponent implements OnInit {
  /** Sólo de lunes a viernes: el fin de semana no se trabaja y el servidor
   *  no acepta permisos en sábado ni domingo. */
  readonly DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie"];
  readonly MESES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ];

  /**
   * Cómo se usa. «calendario» es la pestaña Permisos: el mes y la
   * bitácora. Con «acciones» no pinta ni consulta nada: sólo presta lo
   * que hace falta para responder un permiso —escoger el goce, firmar el
   * FRC-017, resolver y bajar el documento— a la pestaña
   * «Autorizaciones» del calendario de vacaciones (13/10/2026), que es
   * donde ahora se enlista lo que espera respuesta.
   */
  @Input() modo: "calendario" | "acciones" = "calendario";

  cargando = false;
  mes = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  semanas: DiaPermiso[][] = [];
  permisos: Permiso[] = [];
  error = "";

  constructor(
    private service: PermisosService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.modo === "calendario") {
      this.cargar();
    }
  }

  /* Los que esperan respuesta, de cualquier mes, se enlistaban aquí,
     arriba de la cuadrícula. Desde el 13/10/2026 van en la pestaña
     «Autorizaciones», junto con las vacaciones. En el mes siguen
     viéndose en su día, y al tocarlos se abre su detalle para
     responderlos. */

  /* ================= Navegación ================= */

  get titulo(): string {
    return this.MESES[this.mes.getMonth()] + " " + this.mes.getFullYear();
  }

  mueve(delta: number): void {
    this.mes = new Date(this.mes.getFullYear(), this.mes.getMonth() + delta, 1);
    this.cargar();
  }

  irAHoy(): void {
    const h = new Date();
    this.mes = new Date(h.getFullYear(), h.getMonth(), 1);
    this.cargar();
  }

  /* ================= Carga ================= */

  cargar(): void {
    this.cargando = true;
    this.error = "";
    // Se pide el mes completo, no la cuadrícula: los días de relleno son
    // de otro mes y sus permisos se ven cuando se navega hasta allá.
    const y = this.mes.getFullYear();
    const m = this.mes.getMonth();
    const desde = this.iso(new Date(y, m, 1));
    const hasta = this.iso(new Date(y, m + 1, 0));

    this.service.calendario(desde, hasta).subscribe({
      next: (res) => {
        this.cargando = false;
        if (res.codigoError) {
          this.error = res.mensajeError || "No se pudo cargar el calendario.";
          this.permisos = [];
          this.arma();
          this.cdr.detectChanges();
          return;
        }
        this.permisos = res.permisos || [];
        this.arma();
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        this.error = "No se pudo conectar con el servidor.";
        this.permisos = [];
        this.arma();
        this.cdr.detectChanges();
      },
    });
    // La bitácora del mismo mes, si está abierta: son dos preguntas
    // distintas sobre los mismos días y el servidor las contesta con
    // listas distintas (la bitácora incluye rechazados y retirados).
    if (this.vista === "bitacora") {
      this.cargaBitacora();
    }
  }

  /* ================= La bitácora ================= */

  /** Qué se está viendo del mes: la cuadrícula o el registro. */
  vista: "calendario" | "bitacora" = "calendario";

  bitacora: Permiso[] = [];
  cargandoBitacora = false;
  paginaBita = 1;
  readonly tamBita = 10;

  /* ---------------- Los tres filtros ----------------
     Sobre el mes que ya está cargado, no contra el servidor: son a lo
     sumo unas decenas de renglones y filtrar aquí responde al instante
     sin una consulta por cada tecla.

     El resultado vive en un campo y NO en un getter: un getter que
     devuelve un arreglo nuevo en cada ciclo de detección hace que la
     lista se repinte sola sin parar. Se recalcula al cambiar un filtro
     o al llegar datos nuevos, que es cuando de verdad cambia. */

  fBusca = "";
  fTipo = "";
  fEstatus = "";

  bitaFiltrada: Permiso[] = [];

  readonly TIPOS = [
    { valor: "FALTA", texto: "Falta el día" },
    { valor: "SALIDA", texto: "Sale antes" },
    { valor: "ENTRADA", texto: "Llega tarde" },
  ];

  readonly ESTADOS = [
    { valor: "PENDIENTE", texto: "Esperando" },
    { valor: "AUTORIZADO", texto: "Autorizado" },
    { valor: "RECHAZADO", texto: "No autorizado" },
    { valor: "CANCELADO", texto: "Retirado" },
  ];

  /** Verdadero si hay algún filtro puesto, para ofrecer limpiarlos. */
  get hayFiltro(): boolean {
    return !!(this.fBusca.trim() || this.fTipo || this.fEstatus);
  }

  filtra(): void {
    const busca = this.fBusca.trim().toLowerCase();
    this.bitaFiltrada = this.bitacora.filter((p) => {
      if (this.fTipo && p.tipo !== this.fTipo) {
        return false;
      }
      if (this.fEstatus && p.estatus !== this.fEstatus) {
        return false;
      }
      if (!busca) {
        return true;
      }
      // Se busca en lo que se ve en el renglón: el nombre, quién
      // resolvió y la nota. Buscar sólo por nombre obligaría a recordar
      // de quién era el permiso que uno busca por su motivo.
      return (
        (p.colaborador || "").toLowerCase().includes(busca) ||
        (p.autorizaNombre || "").toLowerCase().includes(busca) ||
        (p.observaciones || "").toLowerCase().includes(busca) ||
        (p.motivo || "").toLowerCase().includes(busca)
      );
    });
    // Al filtrar, la página vuelve al principio: quedarse en la 3 de una
    // lista que ahora tiene una deja la pantalla en blanco.
    this.paginaBita = 1;
    this.cdr.detectChanges();
  }

  limpiaFiltros(): void {
    this.fBusca = "";
    this.fTipo = "";
    this.fEstatus = "";
    this.filtra();
  }

  verVista(cual: "calendario" | "bitacora"): void {
    if (this.vista === cual) {
      return;
    }
    this.vista = cual;
    // Se trae al abrirla y no junto con el calendario: quien sólo mira
    // el mes no tiene por qué pagar una consulta que no va a ver.
    if (cual === "bitacora" && !this.bitacora.length) {
      this.cargaBitacora();
    }
    this.cdr.detectChanges();
  }

  cargaBitacora(): void {
    this.cargandoBitacora = true;
    const y = this.mes.getFullYear();
    const m = this.mes.getMonth();
    const desde = this.iso(new Date(y, m, 1));
    const hasta = this.iso(new Date(y, m + 1, 0));

    this.service.calendario(desde, hasta, true).subscribe({
      next: (res) => {
        this.cargandoBitacora = false;
        this.bitacora = res.codigoError ? [] : res.permisos || [];
        // Los filtros se conservan al cambiar de mes: quien anda
        // buscando a una persona la sigue buscando en el mes siguiente.
        this.filtra();
      },
      error: () => {
        this.cargandoBitacora = false;
        this.bitacora = [];
        this.filtra();
      },
    });
  }

  /** El renglón de la bitácora: lo pedido, en una línea. */
  bitaQue(p: Permiso): string {
    if (p.tipo === "FALTA") {
      return "Falta todo el día";
    }
    return (p.tipo === "SALIDA" ? "Sale a las " : "Llega a las ") + (p.hora || "");
  }

  /** La nota del renglón: el motivo del rechazo pesa más que la
   *  observación, porque es la respuesta y no la petición. */
  bitaNota(p: Permiso): string {
    if (p.estatus === "RECHAZADO" && p.motivo) {
      return p.motivo;
    }
    return p.observaciones || "";
  }

  esMotivo(p: Permiso): boolean {
    return p.estatus === "RECHAZADO" && !!p.motivo;
  }

  /** El modificador del badge de estatus: los globales de
   *  table-page__status-badge (styles.css), que los temas ya pintan. */
  claseEstatus(p: Permiso): string {
    switch (p.estatus) {
      case "AUTORIZADO":
        return "is-active";
      case "RECHAZADO":
        return "is-danger";
      case "CANCELADO":
        return "is-inactive";
      default:
        return "is-pending";
    }
  }

  textoEstatus(p: Permiso): string {
    switch (p.estatus) {
      case "AUTORIZADO":
        return "Autorizado";
      case "RECHAZADO":
        return "No autorizado";
      case "CANCELADO":
        return "Retirado";
      default:
        return "Esperando";
    }
  }

  /** "12 sep" — la fecha corta de la primera columna. */
  fechaCorta(iso?: string): string {
    if (!iso) {
      return "";
    }
    const p = iso.substring(0, 10).split("-");
    const mes = (this.MESES[Number(p[1]) - 1] || "").substring(0, 3);
    return Number(p[2]) + " " + mes.toLowerCase();
  }

  /* ---------------- Paginación de la bitácora ---------------- */

  bitaPagina(): Permiso[] {
    const inicio = (this.bitaNorm() - 1) * this.tamBita;
    return this.bitaFiltrada.slice(inicio, inicio + this.tamBita);
  }

  bitaTotalPaginas(): number {
    return this.bitaFiltrada.length > 0
      ? Math.ceil(this.bitaFiltrada.length / this.tamBita)
      : 1;
  }

  bitaNorm(): number {
    return Math.min(Math.max(this.paginaBita, 1), this.bitaTotalPaginas());
  }

  bitaInicio(): number {
    return this.bitaFiltrada.length === 0 ? 0 : (this.bitaNorm() - 1) * this.tamBita + 1;
  }

  bitaFin(): number {
    return Math.min(this.bitaNorm() * this.tamBita, this.bitaFiltrada.length);
  }

  bitaIr(p: number): void {
    const total = this.bitaTotalPaginas();
    if (p < 1 || p > total || p === this.bitaNorm()) {
      return;
    }
    this.paginaBita = p;
    this.cdr.detectChanges();
  }

  bitaPaginas(): Array<number | string> {
    const total = this.bitaTotalPaginas();
    if (total <= 1) {
      return [1];
    }
    const a = this.bitaNorm();
    const set = new Set<number>([1, total, a - 1, a, a + 1]);
    const ord = Array.from(set).filter((p) => p >= 1 && p <= total).sort((x, y) => x - y);
    const items: Array<number | string> = [];
    for (let k = 0; k < ord.length; k++) {
      const p = ord[k];
      const prev = ord[k - 1];
      if (prev && p - prev > 1) {
        items.push("e" + prev);
      }
      items.push(p);
    }
    return items;
  }

  esPagina(item: number | string): item is number {
    return typeof item === "number";
  }

  /* ================= La cuadrícula ================= */

  /** Las semanas del mes, de lunes a viernes (la misma regla que Vacaciones). */
  private arma(): void {
    const hoy = this.iso(new Date());
    this.semanas = semanasHabiles<DiaPermiso>(
      this.mes.getFullYear(),
      this.mes.getMonth(),
      (f) => {
        const iso = this.iso(f);
        return {
          iso,
          numero: f.getDate(),
          esHoy: iso === hoy,
          permisos: this.permisos.filter((p) => (p.fecha || "").substring(0, 10) === iso),
        };
      },
      () => ({ iso: null, numero: null, esHoy: false, permisos: [] }),
    );
  }

  /* ================= El detalle, y resolver desde aquí =================
     El permiso ya se veía en el calendario, pero para responderlo había
     que salir a la pestaña de Permisos y buscarlo en la lista. Se abre
     aquí mismo: clic en el permiso, el detalle, y desde ahí se autoriza
     o se rechaza. Es el mismo flujo de la pestaña —escoger goce, firmar,
     resolver— porque el papel que sale tiene que ser el mismo. */

  /** Se resolvió uno: quien contiene el calendario recuenta su aviso. */
  @Output() resuelto = new EventEmitter<void>();

  /** El permiso abierto en el detalle, o null. */
  abierto: Permiso | null = null;
  resolviendo: number | null = null;
  generandoFrc: number | null = null;

  firmaAbierta = false;
  firmaObjetivo: Permiso | null = null;
  private goceElegido: GocePermiso | null = null;

  abre(p: Permiso): void {
    this.abierto = p;
  }

  cierra(): void {
    this.abierto = null;
  }

  /** Sólo lo que espera respuesta, y no el propio: nadie se autoriza a
   *  sí mismo. El servidor lo niega igual, pero después de haber pedido
   *  el goce y la firma completa. */
  puedeResolver(p: Permiso | null): boolean {
    return !!p && p.estatus === "PENDIENTE" && !p.esMio;
  }

  /** Pendiente y propio: se explica por qué no hay botones. */
  esPropioPendiente(p: Permiso | null): boolean {
    return !!p && p.estatus === "PENDIENTE" && p.esMio === true;
  }

  /** Hay FRC-017 de lo firmado y no negado. */
  tieneFrc(p: Permiso | null): boolean {
    return (
      !!p &&
      p.tieneFirma === true &&
      (p.estatus === "PENDIENTE" || p.estatus === "AUTORIZADO")
    );
  }

  autorizar(p: Permiso): void {
    // El goce lo decide quien autoriza, no quien pide: es una concesión
    // de la empresa. Por eso se pregunta aquí y no en la solicitud.
    Swal.fire({
      title: "¿Cómo se autoriza?",
      html:
        // #475569 y no #64748b: el gris de antes no se leía (24/09/2026).
        '<div style="font-size:13.5px;color:#475569">' +
        (p.colaborador || "") +
        "<br />" +
        this.resumenCorto(p) +
        "</div>",
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: GOCES[0].texto,
      denyButtonText: GOCES[1].texto,
      cancelButtonText: "Cancelar",
    }).then((r) => {
      if (r.isConfirmed) {
        this.goceElegido = "CON";
      } else if (r.isDenied) {
        this.goceElegido = "SIN";
      } else {
        return;
      }
      this.firmaObjetivo = p;
      this.firmaAbierta = true;
      // Se llega desde el .then de un swal, fuera del ciclo de Angular.
      this.cdr.detectChanges();
    });
  }

  rechazar(p: Permiso): void {
    Swal.fire({
      title: "¿Por qué no se autoriza?",
      input: "textarea",
      inputPlaceholder: "El colaborador va a leer esto.",
      inputAttributes: { maxlength: "300" },
      showCancelButton: true,
      confirmButtonText: "No autorizar",
      cancelButtonText: "Cancelar",
      inputValidator: (v) => (v && v.trim() ? null : "Escribe el motivo."),
    }).then((r) => {
      if (r.isConfirmed) {
        this.resuelve(p, false, r.value);
      }
    });
  }

  cierraFirma(): void {
    this.firmaAbierta = false;
    this.firmaObjetivo = null;
    this.cdr.detectChanges();
  }

  firmada(firma: string): void {
    const objetivo = this.firmaObjetivo;
    this.cierraFirma();
    if (objetivo) {
      this.resuelve(objetivo, true, undefined, firma);
    }
  }

  private resuelve(p: Permiso, aprobar: boolean, motivo?: string, firma?: string): void {
    if (p.idPermiso == null) {
      return;
    }
    this.resolviendo = p.idPermiso;
    const goce = aprobar ? this.goceElegido || undefined : undefined;
    this.service.resolver(p.idPermiso, aprobar, motivo, firma, goce).subscribe({
      next: (res) => {
        this.resolviendo = null;
        if (res.codigoError) {
          this.avisa(res.mensajeError || "No se pudo resolver.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.goceElegido = null;
        this.cierra();
        this.resuelto.emit();
        this.avisa(aprobar ? "Permiso autorizado." : "Permiso no autorizado.", "success");
        // Al resolverlo cambia de color en el mes, y si se rechazó
        // desaparece: el calendario pinta lo que va a pasar ese día.
        if (this.modo === "calendario") {
          this.cargar();
        }
        // Igual que en la pestaña: quien firma en papel necesita el papel.
        if (firma === FirmaFrcComponent.FISICA && p.idPermiso != null) {
          this.bajaFrc(p.idPermiso);
        }
      },
      error: () => {
        this.resolviendo = null;
        this.avisa("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  frcDe(p: Permiso): void {
    if (p.idPermiso != null) {
      this.bajaFrc(p.idPermiso);
    }
  }

  private bajaFrc(idPermiso: number): void {
    this.generandoFrc = idPermiso;
    this.service.frc(idPermiso).subscribe({
      next: (res) => {
        this.generandoFrc = null;
        const dato = (res.permisos || [])[0];
        if (res.codigoError || !dato) {
          this.avisa(res.mensajeError || "No se pudo armar el FRC-017.", "error");
          this.cdr.detectChanges();
          return;
        }
        generaFrc017(dato);
        this.cdr.detectChanges();
      },
      error: () => {
        this.generandoFrc = null;
        this.avisa("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  private avisa(texto: string, icono: "success" | "error" | "warning"): void {
    Swal.fire({ text: texto, icon: icono, confirmButtonText: "Entendido" });
  }

  /** "Falta el día", "Sale a las 14:30"… para los títulos. */
  resumenCorto(p: Permiso): string {
    if (p.tipo === "FALTA") {
      return "Falta todo el día";
    }
    return (p.tipo === "SALIDA" ? "Sale a las " : "Llega a las ") + (p.hora || "");
  }

  /** La fecha en largo, para el detalle. */
  fechaLarga(iso?: string): string {
    if (!iso) {
      return "";
    }
    const p = iso.substring(0, 10).split("-");
    const mes = this.MESES[Number(p[1]) - 1] || "";
    return Number(p[2]) + " de " + mes.toLowerCase() + " de " + p[0];
  }

  etiquetaEstatus(p: Permiso): string {
    if (p.estatus === "PENDIENTE") {
      return "Espera respuesta";
    }
    return p.estatus === "AUTORIZADO" ? "Autorizado" : "No autorizado";
  }

  /* ================= Presentación ================= */

  icono(p: Permiso): string {
    if (p.tipo === "FALTA") {
      return "event_busy";
    }
    return p.tipo === "SALIDA" ? "logout" : "login";
  }

  /** La clase pinta el tipo; el estatus sólo atenúa lo que aún no se resuelve. */
  clase(p: Permiso): string {
    const tipo =
      p.tipo === "FALTA" ? "es-falta" : p.tipo === "SALIDA" ? "es-salida" : "es-entrada";
    return tipo + (p.estatus === "PENDIENTE" ? " es-pendiente" : "");
  }

  /** Las iniciales: en una celda de calendario no cabe un nombre completo. */
  iniciales(nombre?: string): string {
    const partes = (nombre || "").trim().split(/\s+/).filter((x) => x.length);
    if (!partes.length) {
      return "?";
    }
    const primera = partes[0][0];
    // El apellido PATERNO: con nombre compuesto es la tercera palabra;
    // con nombre simple, la segunda. Tomar la última daría el materno.
    const apellido = partes.length >= 4 ? partes[2] : partes[1];
    return (primera + (apellido ? apellido[0] : "")).toUpperCase();
  }

  tip(p: Permiso): string {
    const lineas: string[] = [p.colaborador || "Colaborador"];
    lineas.push(
      p.tipo === "FALTA"
        ? "Falta todo el día"
        : p.tipo === "SALIDA"
          ? "Sale a las " + (p.hora || "")
          : "Llega a las " + (p.hora || ""),
    );
    if (p.goce) {
      lineas.push(p.goce === "CON" ? "Con goce de sueldo" : "Sin goce de sueldo");
    }
    lineas.push(p.estatus === "PENDIENTE" ? "Esperando respuesta" : "Autorizado");
    if (p.observaciones) {
      lineas.push("“" + p.observaciones + "”");
    }
    return lineas.join("\n");
  }

  /** Cuántos de cada cosa en el mes, para la banda de arriba. */
  get resumen(): Array<{ etiqueta: string; cuantos: number; clase: string }> {
    const de = (t: string) => this.permisos.filter((p) => p.tipo === t).length;
    // Singular cuando es uno: "1 salidas" se lee mal en una banda que
    // justamente está para leerse de un vistazo.
    const n = (c: number, uno: string, varios: string) => (c === 1 ? uno : varios);
    const f = de("FALTA");
    const s = de("SALIDA");
    const e = de("ENTRADA");
    return [
      { etiqueta: n(f, "falta", "faltas"), cuantos: f, clase: "es-falta" },
      { etiqueta: n(s, "salida", "salidas"), cuantos: s, clase: "es-salida" },
      { etiqueta: n(e, "entrada", "entradas"), cuantos: e, clase: "es-entrada" },
    ];
  }

  get pendientes(): number {
    return this.permisos.filter((p) => p.estatus === "PENDIENTE").length;
  }

  private iso(d: Date): string {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return d.getFullYear() + "-" + mm + "-" + dd;
  }

  trackDia = (i: number, d: DiaPermiso) => d.iso || "x" + i;
  trackPermiso = (_: number, p: Permiso) => p.idPermiso;
}
